import React, { useEffect, useState } from "react";
import {
  Package,
  ChevronLeft,
  ChevronRight,
  Trash2,
  CheckCircle,
  XCircle,
  Edit,
  AlertCircle,
  Send,
  Truck,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import RejectStockRequisition from "./RejectStockRequisition";
import UpdateRequisition from "./UpdateRequisition";
import ExportData from "../../components/common/ExportData";
import DebouncedSearchInput from "../../components/common/DebouncedSearchInput";
import { useGlobalContext } from "../../context/stateContext";
import RoleBasedComponent from "../../components/RoleBasedComponent";

const StockRequisition = () => {
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [requisitionItems, setRequisitionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [removingItem, setRemovingItem] = useState(false);
  const [approvingItem, setApprovingItem] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [status, setStatus] = useState("Requested");
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);

  const fetchItemsList = async (page_size, search_query, current_page, status_filter) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/requisition/branch/${activeSession?.branch?.id}?page=${current_page}&pageSize=${page_size}&search=${search_query}&status=${status_filter}`,
        "GET"
      );
      setRequisitionItems(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (status_filter) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/requisition/branch/${activeSession?.branch?.id}?paginated=false&status=${status_filter}`,
        "GET"
      );

      let inventoryData = [];

      for (let i = 0; i <= data?.length - 1; i++) {
        inventoryData.push({
          ID: data[i]?.id,
          Drug: data[i]?.drug,
          "Quantity Requested": data[i]?.quantity,
          Branch: data[i]?.branch?.name,
          "Date Requested": data[i]?.created_at,
        });
      }
      setTableData(inventoryData);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (item_id) => {
    Swal.fire({
      icon: "question",
      title: "Are you sure you want to delete this item?",
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setRemovingItem(true);
          const data = await requestWithAuth(
            `/inventory/requisition/branch/${item_id}/item`,
            "DELETE"
          );
          Swal.fire({
            icon: "success",
            title: "Requested!",
            text: "Item Removed Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something went wrong!");
        } finally {
          setRemovingItem(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const approveItem = async (item_id) => {
    Swal.fire({
      icon: "question",
      title: "Do you confirm that you have received this item?",
      showDenyButton: true,
      confirmButtonText: "Yes Confirm!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setApprovingItem(true);
          const data = await requestWithAuth(
            `/inventory/requisition/branch/${item_id}/item?action=approve`,
            "PUT",
            {}
          );
          Swal.fire({
            icon: "success",
            title: "Requested!",
            text: "Item Approved Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something went wrong!");
        } finally {
          setApprovingItem(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  // Determine what actions are available based on status
  const getAvailableActions = (status) => {
    switch (status) {
      case "Requested":
        return {
          canUpdate: true,
          canRemove: true,
          canApprove: false,
          canReject: false,
        };
      case "Supplied":
        return {
          canUpdate: false,
          canRemove: false,
          canApprove: false,
          canReject: false,
        };
      case "Dispatched":
        return {
          canUpdate: false,
          canRemove: false,
          canApprove: true,
          canReject: true,
        };
      case "Approved":
      case "Rejected":
        return {
          canUpdate: false,
          canRemove: false,
          canApprove: false,
          canReject: false,
        };
      default:
        return {
          canUpdate: false,
          canRemove: false,
          canApprove: false,
          canReject: false,
        };
    }
  };

  const filterItemsList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Requested: { color: "bg-yellow-100 text-yellow-800", icon: AlertCircle },
      Supplied: { color: "bg-blue-100 text-blue-800", icon: Truck },
      Dispatched: { color: "bg-purple-100 text-purple-800", icon: Send },
      Approved: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      Rejected: { color: "bg-red-100 text-red-800", icon: XCircle },
    };

    const config = statusConfig[status] || {
      color: "bg-gray-100 text-gray-800",
      icon: AlertCircle,
    };
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        <Icon size={14} />
        {status}
      </span>
    );
  };

  useEffect(() => {

    if (activeSession) {
      fetchItemsList(pageSize, search, currentPage, status);
      getTableData(pageSize, search, currentPage, status);
    }
  }, [search, currentPage, pageSize, activeSession, status]);

  useEffect(() => {
    if (activeSession) {
      getTableData(status);
    }
  }, [activeSession, status]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {rejectionModalOpen && (
        <RejectStockRequisition
          modalOpen={rejectionModalOpen}
          setModalOpen={setRejectionModalOpen}
          item={selectedDrug}
        />
      )}
      {updateModalOpen && (
        <UpdateRequisition
          modalOpen={updateModalOpen}
          setModalOpen={setUpdateModalOpen}
          item={selectedDrug}
        />
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <Link
                  to="/inventory/requisition"
                  className="hover:text-gray-700"
                >
                  Requisitions
                </Link>
                <span>/</span>
                <span className="text-gray-900 font-medium">Details</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Requisition Details
              </h1>
            </div>
            <RoleBasedComponent allowedRoles={["dispenser", "branch_manager"]}>
              <Link
                to="/inventory/requisition/new "
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
              >
                <Plus size={20} />
                New Requisition
              </Link>
            </RoleBasedComponent>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData
                  tableData={tableData}
                  fileName="Requisition Details"
                />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>

                <div className="h-8 w-[1px] bg-gray-200 mx-2 hidden sm:block"></div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 whitespace-nowrap">Filter Status:</span>
                  <select
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white font-medium"
                    value={status}
                  >
                    <option value="">All Status</option>
                    <option value="Requested">Requested</option>
                    <option value="Supplied">Supplied</option>
                    <option value="Dispatched">Dispatched</option>
                    <option value="Approved">Approved</option>
                  </select>
                </div>
              </div>

              <DebouncedSearchInput
                onSearch={filterItemsList}
                placeholder="Search items..."
                delay={500}
                className="flex-1 max-w-md"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading requisition items...</span>
                </div>
              </div>
            ) : requisitionItems?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Item Name
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Qty Requested
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Qty Supplied
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Requisition Date
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Rejection Reason
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {requisitionItems?.map((item, index) => {
                    const actions = getAvailableActions(item?.status);

                    return (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6 text-sm text-gray-900">
                          {item?.id}
                        </td>
                        <td className="py-4 px-6 text-sm font-medium text-gray-900">
                          {item?.drug}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {item?.quantity}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {item?.quantity_supplied ? (
                            <span className="font-medium text-gray-900">
                              {item?.quantity_supplied}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">
                              Not supplied
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {item?.created_at}
                        </td>
                        <td className="py-4 px-6">
                          {getStatusBadge(item?.status)}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {item?.rejection_reason ? (
                            <span className="text-red-600">
                              {item?.rejection_reason}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">
                              N/A
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          {removingItem || approvingItem ? (
                            <div className="flex items-center gap-2 text-gray-500">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                              <span className="text-xs">Processing...</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {/* Update Button - only for Requested */}
                              {actions.canUpdate && (
                                <button
                                  onClick={() => {
                                    setSelectedDrug(item);
                                    setUpdateModalOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                >
                                  <Edit size={14} />
                                  Update
                                </button>
                              )}

                              {/* Remove Button - only for Requested */}
                              {actions.canRemove && (
                                <button
                                  onClick={() => removeItem(item?.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                  <Trash2 size={14} />
                                  Remove
                                </button>
                              )}

                              {/* Reject Button - only for Dispatched */}
                              {actions.canReject && (
                                <button
                                  onClick={() => {
                                    setSelectedDrug(item);
                                    setRejectionModalOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                  <XCircle size={14} />
                                  Reject
                                </button>
                              )}

                              {/* Approve Button - only for Dispatched */}
                              {actions.canApprove && (
                                <button
                                  onClick={() => approveItem(item?.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                                >
                                  <CheckCircle size={14} />
                                  Approve
                                </button>
                              )}

                              {/* No actions available */}
                              {!actions.canUpdate &&
                                !actions.canRemove &&
                                !actions.canApprove &&
                                !actions.canReject && (
                                  <span className="text-xs text-gray-400 italic">
                                    No actions
                                  </span>
                                )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <Package className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-sm">
                  No requisition items found
                </p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {requisitionItems?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockRequisition;
